import { useEffect, useMemo, useState } from 'react';

import { supabase } from '@/lib/supabase';

type ParsedRow = Record<string, string>;

const requiredHeaders = [
  'first_name',
  'last_name',
  'phone',
  'zip',
  'membership_status',
  'last_service_date',
  'campaign_id',
];

function parseCsv(text: string): { headers: string[]; rows: ParsedRow[] } {
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter(Boolean);
  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = lines[0].split(',').map((h) => h.trim());
  const rows: ParsedRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map((c) => c.trim());
    const row: ParsedRow = {};
    headers.forEach((h, idx) => {
      row[h] = cols[idx] ?? '';
    });
    rows.push(row);
  }
  return { headers, rows };
}

export default function BatchCallingPage() {
  const [fileName, setFileName] = useState<string | null>(null);
  const [rawCsv, setRawCsv] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // Status monitor state
  const [statusData, setStatusData] = useState<any | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [campaignId, setCampaignId] = useState('');
  const [jobId, setJobId] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [poll, setPoll] = useState(false);
  const [pollMs, setPollMs] = useState(5000);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  // History UI state
  const [companyId, setCompanyId] = useState('');
  const [historyData, setHistoryData] = useState<any | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLimit, setHistoryLimit] = useState(25);

  // Normalize upstream status into tabular rows for display
  const statusRows = useMemo(() => {
    const u: any = (statusData as any)?.upstream ?? (statusData as any);
    const jobs: any[] = Array.isArray(u?.jobs)
      ? u.jobs
      : Array.isArray(u?.data)
      ? u.data
      : Array.isArray(u?.items)
      ? u.items
      : u?.job
      ? [u.job]
      : [];

    const mapJob = (j: any) => ({
      job_id: j?.job_id ?? j?.id ?? '',
      campaign_id: j?.campaign_id ?? campaignId ?? '',
      state: j?.state ?? j?.status ?? '',
      queued: j?.queued ?? j?.totals?.queued ?? j?.counts?.queued ?? null,
      running: j?.running ?? j?.totals?.running ?? j?.counts?.running ?? null,
      succeeded: j?.succeeded ?? j?.totals?.succeeded ?? j?.counts?.succeeded ?? null,
      failed: j?.failed ?? j?.totals?.failed ?? j?.counts?.failed ?? null,
      created_at: j?.created_at ?? j?.createdAt ?? '',
      updated_at: j?.updated_at ?? j?.updatedAt ?? '',
    });

    return jobs.map(mapJob);
  }, [statusData, campaignId]);

  const parsed = useMemo(() => {
    if (!rawCsv) return { headers: [], rows: [] as ParsedRow[] };
    try {
      return parseCsv(rawCsv);
    } catch (e) {
      return { headers: [], rows: [] as ParsedRow[] };
    }
  }, [rawCsv]);

  const headerStatus = useMemo(() => {
    const missing = requiredHeaders.filter((h) => !parsed.headers.includes(h));
    return { missing, ok: missing.length === 0 };
  }, [parsed.headers]);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    try {
      const text = await f.text();
      setRawCsv(text);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to read file');
    }
  };

  const canSchedule = parsed.rows.length > 0 && headerStatus.ok;

  const sample = useMemo(() => parsed.rows.slice(0, 5), [parsed.rows]);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    setStatusError(null);
    try {
      const { data, error } = await supabase.functions.invoke('elevenlabs-batch-calling-status-ui', {
        body: { campaign_id: campaignId || undefined, job_id: jobId || undefined },
      });
      if (error) throw error;
      setStatusData(data);
      setLastUpdated(Date.now());
    } catch (e: any) {
      setStatusError(e?.message || 'Failed to fetch status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    setHistoryError(null);
    try {
      const { data, error } = await supabase.functions.invoke('elevenlabs-batch-calling-history-ui', {
        body: {
          company_id: companyId || undefined,
          campaign_id: campaignId || undefined,
          limit: historyLimit,
        },
      });
      if (error) throw error;
      setHistoryData(data);
    } catch (e: any) {
      setHistoryError(e?.message || 'Failed to fetch history');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (!poll) return;
    // Kick off immediate fetch, then interval
    fetchStatus();
    const id = setInterval(fetchStatus, pollMs);
    return () => clearInterval(id);
  }, [poll, pollMs, campaignId, jobId]);

  const handleSchedule = async () => {
    setError(null);
    try {
      // Validate campaign_id column across rows (expect exactly one campaign per upload)
      const uniqueCampaigns = Array.from(
        new Set(parsed.rows.map((r) => (r['campaign_id'] || '').trim()).filter(Boolean))
      );
      if (uniqueCampaigns.length !== 1) {
        setError(
          uniqueCampaigns.length === 0
            ? 'CSV missing campaign_id values.'
            : 'Multiple campaign_id values detected; expected one campaign per upload.'
        );
        return;
      }
      const campaign = uniqueCampaigns[0];

      const idempotency_key = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const { data, error } = await supabase.functions.invoke('elevenlabs-batch-calling-submit-ui', {
        body: {
          campaign_id: campaign,
          rows: parsed.rows,
          idempotency_key,
        },
      });
      if (error) throw error;

      // Attempt to extract job_id from common response shapes
      let newJobId: string =
        (data?.job_id as string) ||
        (data?.upstream?.job_id as string) ||
        (data?.upstream?.data?.job_id as string) ||
        '';

      setCampaignId(campaign);
      if (newJobId) setJobId(newJobId);
      setStatusData(data);
      setPoll(true); // start polling automatically
    } catch (e: any) {
      setError(e?.message || 'Failed to schedule batch');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Batch Calling (ElevenLabs)</h1>
      </div>

      <div className="rounded border p-4 space-y-3 bg-white">
        <p className="text-sm text-gray-700">
          Upload a CSV with the following headers:
          <code className="ml-1 text-xs bg-gray-100 px-1 py-0.5 rounded">
            {requiredHeaders.join(', ')}
          </code>
        </p>

        <div className="flex items-center gap-3">
          <input type="file" accept=".csv" onChange={onFileChange} />
          {fileName && (
            <span className="text-xs text-gray-600">Selected: {fileName}</span>
          )}
        </div>

        {error && (
          <div className="text-red-600 text-sm">{error}</div>
        )}

        <div className="text-sm">
          <div className="font-medium">Header validation:</div>
          {headerStatus.ok ? (
            <div className="text-green-700">All required headers present.</div>
          ) : (
            <div className="text-amber-700">
              Missing headers: {headerStatus.missing.join(', ') || '—'}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            className={`px-3 py-1.5 rounded text-white ${canSchedule ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}
            disabled={!canSchedule}
            onClick={handleSchedule}
          >
            Schedule Batch
          </button>

          <button
            className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setFileName(null);
              setRawCsv('');
              setError(null);
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="rounded border p-4 bg-white">
        <div className="font-medium mb-2">Preview (first 5 rows)</div>
        {parsed.rows.length === 0 ? (
          <div className="text-sm text-gray-600">No data.</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  {parsed.headers.map((h) => (
                    <th key={h} className="text-left px-2 py-1 border-b bg-gray-50">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sample.map((row, i) => (
                  <tr key={i} className="odd:bg-white even:bg-gray-50">
                    {parsed.headers.map((h) => (
                      <td key={h} className="px-2 py-1 border-b">{row[h] ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-gray-600 mt-2">
              Showing {sample.length} of {parsed.rows.length} rows.
            </div>
          </div>
        )}
      </div>

      <div className="rounded border p-4 bg-white space-y-3">
        <div className="font-medium">Batch Calling Status</div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-xs text-gray-600">campaign_id</label>
            <input
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
              placeholder="optional"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600">job_id</label>
            <input
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
              placeholder="optional"
            />
          </div>
          <button
            className="px-3 py-1.5 rounded text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
            disabled={loadingStatus}
            onClick={fetchStatus}
          >
            {loadingStatus ? 'Loading…' : 'Fetch Status'}
          </button>
          <label className="inline-flex items-center gap-1 text-sm ml-2">
            <input
              type="checkbox"
              checked={poll}
              onChange={(e) => setPoll(e.target.checked)}
            />
            Auto-refresh
          </label>
          <input
            type="number"
            className="border rounded px-2 py-1 text-sm w-24"
            value={pollMs}
            onChange={(e) => setPollMs(Math.max(1000, Number(e.target.value) || 5000))}
            title="Polling interval (ms)"
          />
          {lastUpdated && (
            <span className="text-xs text-gray-600">
              Last updated: {new Date(lastUpdated).toLocaleTimeString()}
            </span>
          )}
        </div>
        {statusError && <div className="text-red-600 text-sm">{statusError}</div>}

        {statusRows.length > 0 && (
          <div className="overflow-auto">
            <table className="min-w-full text-xs mb-2">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-2 py-1 border-b">job_id</th>
                  <th className="text-left px-2 py-1 border-b">campaign_id</th>
                  <th className="text-left px-2 py-1 border-b">state</th>
                  <th className="text-right px-2 py-1 border-b">queued</th>
                  <th className="text-right px-2 py-1 border-b">running</th>
                  <th className="text-right px-2 py-1 border-b">succeeded</th>
                  <th className="text-right px-2 py-1 border-b">failed</th>
                  <th className="text-left px-2 py-1 border-b">created_at</th>
                  <th className="text-left px-2 py-1 border-b">updated_at</th>
                </tr>
              </thead>
              <tbody>
                {statusRows.map((r, i) => (
                  <tr key={r.job_id ? `${r.job_id}-${i}` : i} className="odd:bg-white even:bg-gray-50">
                    <td className="px-2 py-1 border-b">{r.job_id || '—'}</td>
                    <td className="px-2 py-1 border-b">{r.campaign_id || '—'}</td>
                    <td className="px-2 py-1 border-b">{r.state || '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{r.queued ?? '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{r.running ?? '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{r.succeeded ?? '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{r.failed ?? '—'}</td>
                    <td className="px-2 py-1 border-b">{r.created_at || '—'}</td>
                    <td className="px-2 py-1 border-b">{r.updated_at || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {statusData ? (
          <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-64">
            {JSON.stringify(statusData, null, 2)}
          </pre>
        ) : (
          <div className="text-sm text-gray-600">No status loaded.</div>
        )}
      </div>

      <div className="rounded border p-4 bg-white space-y-3">
        <div className="font-medium">Batch History</div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-xs text-gray-600">company_id</label>
            <input
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
              placeholder="optional"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600">campaign_id</label>
            <input
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
              placeholder="optional"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600">limit</label>
            <input
              type="number"
              className="border rounded px-2 py-1 text-sm w-24"
              value={historyLimit}
              onChange={(e) => setHistoryLimit(Math.max(1, Math.min(200, Number(e.target.value) || 25)))}
              title="Max records"
            />
          </div>
          <button
            className="px-3 py-1.5 rounded text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
            disabled={loadingHistory}
            onClick={fetchHistory}
          >
            {loadingHistory ? 'Loading…' : 'Fetch History'}
          </button>
          {historyError && <div className="text-red-600 text-sm">{historyError}</div>}
        </div>

        {historyData?.jobs?.length > 0 && (
          <div className="overflow-auto">
            <div className="font-medium mb-1 text-sm">Jobs</div>
            <table className="min-w-full text-xs mb-4">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-2 py-1 border-b">created_at</th>
                  <th className="text-left px-2 py-1 border-b">company_id</th>
                  <th className="text-left px-2 py-1 border-b">campaign_id</th>
                  <th className="text-left px-2 py-1 border-b">job_id</th>
                  <th className="text-left px-2 py-1 border-b">state</th>
                  <th className="text-right px-2 py-1 border-b">total</th>
                  <th className="text-right px-2 py-1 border-b">valid</th>
                  <th className="text-right px-2 py-1 border-b">invalid</th>
                  <th className="text-right px-2 py-1 border-b">filtered_quiet</th>
                  <th className="text-right px-2 py-1 border-b">filtered_dnc</th>
                  <th className="text-right px-2 py-1 border-b">submitted</th>
                </tr>
              </thead>
              <tbody>
                {historyData.jobs.map((j: any) => (
                  <tr key={j.id} className="odd:bg-white even:bg-gray-50">
                    <td className="px-2 py-1 border-b">{j.created_at}</td>
                    <td className="px-2 py-1 border-b">{j.company_id}</td>
                    <td className="px-2 py-1 border-b">{j.campaign_id}</td>
                    <td className="px-2 py-1 border-b">{j.job_id || '—'}</td>
                    <td className="px-2 py-1 border-b">{j.state || '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{j.total ?? '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{j.valid ?? '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{j.invalid ?? '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{j.filtered_quiet ?? '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{j.filtered_dnc ?? '—'}</td>
                    <td className="px-2 py-1 border-b text-right">{j.submitted ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {historyData?.snapshots?.length > 0 && (
          <div className="overflow-auto">
            <div className="font-medium mb-1 text-sm">Snapshots</div>
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-2 py-1 border-b">created_at</th>
                  <th className="text-left px-2 py-1 border-b">company_id</th>
                  <th className="text-left px-2 py-1 border-b">campaign_id</th>
                  <th className="text-left px-2 py-1 border-b">job_id</th>
                  <th className="text-left px-2 py-1 border-b">state</th>
                  <th className="text-left px-2 py-1 border-b">totals</th>
                </tr>
              </thead>
              <tbody>
                {historyData.snapshots.map((s: any) => (
                  <tr key={s.id} className="odd:bg-white even:bg-gray-50">
                    <td className="px-2 py-1 border-b">{s.created_at}</td>
                    <td className="px-2 py-1 border-b">{s.company_id || '—'}</td>
                    <td className="px-2 py-1 border-b">{s.campaign_id || '—'}</td>
                    <td className="px-2 py-1 border-b">{s.job_id || '—'}</td>
                    <td className="px-2 py-1 border-b">{s.state || '—'}</td>
                    <td className="px-2 py-1 border-b">{s.totals ? JSON.stringify(s.totals) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!historyData && !historyError && (
          <div className="text-sm text-gray-600">No history loaded.</div>
        )}
      </div>

      <div className="rounded border p-4 bg-white space-y-2">
        <div className="font-medium">Next steps (server integration)</div>
        <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
          <li>Create/verify Supabase Edge Function for Batch Calling submission (POST /v1/convai/batch-calling/submit).</li>
          <li>Enforce DNC scrubbing, quiet-hours, and consent logging before enqueue.</li>
          <li>Persist job and item statuses; add status polling endpoint/UI refresh.</li>
          <li>Respect concurrency and plan limits; surface requeue/retry UI affordances.</li>
        </ul>
      </div>
    </div>
  );
}
